import { Button, Chip, faDigits, Field, formatDate, formatMoney, Panel } from "@kitchen/ui";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { api, hrApi, type PersonDocumentRow, type PersonnelFileRow } from "../../api.js";
import { askReason, ErrorLine, useAction } from "../common.js";

const EMPLOYMENT_KIND_LABELS: Record<PersonnelFileRow["employmentKind"], string> = {
  official: "رسمی",
  contractual: "قراردادی",
  daily: "روزمزد",
  temporary: "موقت",
};

interface StaffMember {
  id: string;
  displayName: string;
  phone: string;
  hasPin: boolean;
  isSupport?: boolean;
}

export function PeopleTab() {
  const [selectedPersonId, setSelectedPersonId] = useState<string | null>(null);
  const [showAddFileModal, setShowAddFileModal] = useState(false);
  const [showAddDocModal, setShowAddDocModal] = useState(false);
  const [editingPay, setEditingPay] = useState(false);

  // Queries
  const peopleQuery = useQuery({
    queryKey: ["hr", "people"],
    queryFn: () => hrApi.listPeople(),
  });

  const positionsQuery = useQuery({
    queryKey: ["hr", "positions"],
    queryFn: () => hrApi.listPositions(),
  });

  const docTypesQuery = useQuery({
    queryKey: ["hr", "document-types"],
    queryFn: () => hrApi.listDocumentTypes(),
  });

  const staffQuery = useQuery({
    queryKey: ["staff"],
    queryFn: () => api<StaffMember[]>("/staff"),
  });

  const activePerson = (peopleQuery.data ?? []).find((p) => p.personId === selectedPersonId) ?? null;

  // Single person file detail query when selected
  const personDetailQuery = useQuery({
    queryKey: ["hr", "people", selectedPersonId],
    queryFn: () => (selectedPersonId ? hrApi.getPersonFile(selectedPersonId) : null),
    enabled: !!selectedPersonId,
  });

  const currentFile = personDetailQuery.data ?? activePerson;

  // Form state: Add Personnel File
  const [formPersonId, setFormPersonId] = useState("");
  const [formPersonName, setFormPersonName] = useState("");
  const [formPositionId, setFormPositionId] = useState("");
  const [formEmploymentKind, setFormEmploymentKind] =
    useState<PersonnelFileRow["employmentKind"]>("official");
  const [formLevel, setFormLevel] = useState("");
  const [formStartDate, setFormStartDate] = useState(new Date().toISOString().slice(0, 10));
  const [formContactName, setFormContactName] = useState("");
  const [formContactPhone, setFormContactPhone] = useState("");
  const [formContactRelation, setFormContactRelation] = useState("خانواده");
  const [formBasePay, setFormBasePay] = useState("");
  const [formHourlyRate, setFormHourlyRate] = useState("");
  const [formNotes, setFormNotes] = useState("");

  // Form state: Add Document
  const [docKind, setDocKind] = useState("health_card");
  const [docNumber, setDocNumber] = useState("");
  const [docIssueDate, setDocIssueDate] = useState("");
  const [docExpiryDate, setDocExpiryDate] = useState("");
  const [docNotes, setDocNotes] = useState("");

  // Form state: Edit Pay
  const [editBasePay, setEditBasePay] = useState("");
  const [editHourlyRate, setEditHourlyRate] = useState("");

  // Actions
  const addFileAction = useAction(async () => {
    const selectedStaff = (staffQuery.data ?? []).find((s) => s.id === formPersonId);
    const name = formPersonName.trim() || selectedStaff?.displayName || "پرسنل";
    const selectedPos = (positionsQuery.data ?? []).find((p) => p.id === formPositionId);

    await hrApi.createPersonnelFile({
      personId: formPersonId,
      personName: name,
      positionId: formPositionId || null,
      positionName: selectedPos?.name ?? "",
      level: formLevel.trim() || undefined,
      employmentKind: formEmploymentKind,
      startDate: formStartDate,
      emergencyContact: {
        name: formContactName.trim() || "نامشخص",
        phone: formContactPhone.trim() || "09140000000",
        relation: formContactRelation.trim() || undefined,
      },
      basePay: formBasePay ? Number.parseInt(formBasePay, 10) : null,
      hourlyRate: formHourlyRate ? Number.parseInt(formHourlyRate, 10) : null,
      notes: formNotes.trim() || undefined,
    });

    setShowAddFileModal(false);
    setSelectedPersonId(formPersonId);
    // Reset form
    setFormPersonId("");
    setFormPersonName("");
    setFormPositionId("");
    setFormLevel("");
    setFormContactName("");
    setFormContactPhone("");
    setFormBasePay("");
    setFormHourlyRate("");
    setFormNotes("");
  }, [["hr", "people"]]);

  const addDocAction = useAction(async () => {
    if (!currentFile) return;
    await hrApi.createDocument({
      personId: currentFile.personId,
      kind: docKind,
      documentNumber: docNumber.trim(),
      issueDate: docIssueDate || null,
      expiryDate: docExpiryDate,
      notes: docNotes.trim() || undefined,
    });
    setShowAddDocModal(false);
    setDocNumber("");
    setDocIssueDate("");
    setDocExpiryDate("");
    setDocNotes("");
  }, [
    ["hr", "people"],
    ["hr", "people", selectedPersonId ?? ""],
    ["hr", "documents"],
  ]);

  const updatePayAction = useAction(async () => {
    if (!currentFile) return;
    await hrApi.updatePersonPay(currentFile.id, {
      version: currentFile.version,
      basePay: editBasePay ? Number.parseInt(editBasePay, 10) : null,
      hourlyRate: editHourlyRate ? Number.parseInt(editHourlyRate, 10) : null,
    });
    setEditingPay(false);
  }, [
    ["hr", "people"],
    ["hr", "people", selectedPersonId ?? ""],
  ]);

  const archiveFileAction = useAction(
    async (file: PersonnelFileRow) => {
      const reason = askReason(`پرونده ${file.personName}`);
      if (!reason) return;
      await hrApi.archivePersonnelFile(file.id, file.version, reason);
      if (selectedPersonId === file.personId) {
        setSelectedPersonId(null);
      }
    },
    [["hr", "people"]],
  );

  const archiveDocAction = useAction(
    async (doc: PersonDocumentRow) => {
      const reason = askReason(`مدرک ${doc.kindLabel || doc.kind}`);
      if (!reason) return;
      await hrApi.archiveDocument(doc.id, doc.version, reason);
    },
    [
      ["hr", "people"],
      ["hr", "people", selectedPersonId ?? ""],
      ["hr", "documents"],
    ],
  );

  return (
    <div className="grid" style={{ gridTemplateColumns: currentFile ? "1fr 1fr" : "1fr", gap: "var(--s4)" }}>
      {/* People List */}
      <Panel
        title="فهرست پرونده‌های پرسنل"
        actions={
          <Button
            variant="primary"
            onClick={() => {
              setShowAddFileModal(true);
            }}
          >
            افزودن پرونده پرسنل
          </Button>
        }
      >
        <div className="card" style={{ padding: "0", overflow: "hidden" }}>
          <table className="tbl">
            <thead>
              <tr>
                <th>نام و کد</th>
                <th>سمت</th>
                <th>نوع همکاری</th>
                <th>تاریخ شروع</th>
                <th>وضعیت مدارک</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {(peopleQuery.data ?? []).length === 0 ? (
                <tr>
                  <td colSpan={6} style={{ textAlign: "center", padding: "var(--s4)" }} className="muted">
                    هنوز پرونده پرسنلی ثبت نشده است. با دکمه‌ی «افزودن پرونده پرسنل» شروع کنید.
                  </td>
                </tr>
              ) : (
                (peopleQuery.data ?? []).map((p) => {
                  const isSelected = p.personId === selectedPersonId;
                  const docs = p.documents ?? [];
                  const hasExpired = docs.some((d) => d.expiryStatus === "expired");
                  const hasSoon = docs.some((d) => d.expiryStatus === "expiring_soon");

                  return (
                    <tr
                      key={p.id}
                      style={{
                        cursor: "pointer",
                        backgroundColor: isSelected ? "var(--bg-subtle, rgba(0,0,0,0.04))" : undefined,
                      }}
                      onClick={() => {
                        setSelectedPersonId(p.personId);
                      }}
                    >
                      <td className="m">
                        <div className="stack" style={{ gap: "2px" }}>
                          <span className="bold">{p.personName}</span>
                          {p.phone && (
                            <span className="num muted t-cap" dir="ltr">
                              {p.phone}
                            </span>
                          )}
                        </div>
                      </td>
                      <td>{p.positionName || "—"}</td>
                      <td>
                        <Chip plain>{EMPLOYMENT_KIND_LABELS[p.employmentKind]}</Chip>
                      </td>
                      <td className="num">{formatDate(p.startDate)}</td>
                      <td>
                        {hasExpired ? (
                          <span
                            className="chip coral"
                            style={{ color: "var(--coral-ink)", background: "var(--coral-soft)" }}
                          >
                            منقضی
                          </span>
                        ) : hasSoon ? (
                          <span
                            className="chip coral"
                            style={{ color: "var(--coral-ink)", background: "var(--coral-soft)" }}
                          >
                            انقضا نزدیک است
                          </span>
                        ) : docs.length > 0 ? (
                          <Chip tone="ok">معتبر</Chip>
                        ) : (
                          <span className="muted t-cap">بدون مدرک</span>
                        )}
                      </td>
                      <td className="end">
                        <Button
                          variant={isSelected ? "primary" : "ghost"}
                          size="md"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedPersonId(p.personId);
                          }}
                        >
                          مشاهده پرونده
                        </Button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </Panel>

      {/* Person Detail View */}
      {currentFile && (
        <Panel
          title={`پرونده ${currentFile.personName}`}
          actions={
            <div className="row" style={{ gap: "var(--s2)" }}>
              <Button
                variant="ghost"
                onClick={() => {
                  setSelectedPersonId(null);
                }}
              >
                بستن
              </Button>
              <Button
                variant="urgent"
                onClick={() => {
                  archiveFileAction.run(currentFile);
                }}
                disabled={archiveFileAction.busy}
              >
                بایگانی پرونده
              </Button>
            </div>
          }
        >
          <div className="stack" style={{ gap: "var(--s4)" }}>
            {/* Overview Card */}
            <div className="card" style={{ padding: "var(--s4)" }}>
              <div className="stack" style={{ gap: "var(--s3)" }}>
                <div className="row wrap-between" style={{ alignItems: "center" }}>
                  <div>
                    <h3 className="t-title">{currentFile.personName}</h3>
                    {currentFile.phone && (
                      <span className="num muted" dir="ltr">
                        {currentFile.phone}
                      </span>
                    )}
                  </div>
                  <Chip tone="blue">{EMPLOYMENT_KIND_LABELS[currentFile.employmentKind]}</Chip>
                </div>

                <div
                  className="grid"
                  style={{ gridTemplateColumns: "1fr 1fr", gap: "var(--s3)", marginTop: "var(--s2)" }}
                >
                  <div>
                    <span className="muted t-cap">سمت سازمانی: </span>
                    <span className="bold">{currentFile.positionName || "تعیین نشده"}</span>
                  </div>
                  {currentFile.level && (
                    <div>
                      <span className="muted t-cap">رده: </span>
                      <span>{currentFile.level}</span>
                    </div>
                  )}
                  <div>
                    <span className="muted t-cap">تاریخ شروع همکاری: </span>
                    <span className="num bold">{formatDate(currentFile.startDate)}</span>
                  </div>
                </div>

                {/* Emergency Contact */}
                <div
                  style={{
                    borderTop: "1px solid var(--border)",
                    paddingTop: "var(--s3)",
                    marginTop: "var(--s2)",
                  }}
                >
                  <span className="bold t-cap">تماس اضطراری: </span>
                  <span>{currentFile.emergencyContact.name}</span>
                  {currentFile.emergencyContact.relation && (
                    <span className="muted"> ({currentFile.emergencyContact.relation})</span>
                  )}
                  <span className="num" dir="ltr" style={{ marginInlineStart: "var(--s3)" }}>
                    {currentFile.emergencyContact.phone}
                  </span>
                </div>

                {/* Pay Section — only displayed if hr.pay returned compensation fields */}
                {("basePay" in currentFile || "hourlyRate" in currentFile) && (
                  <div
                    style={{
                      borderTop: "1px solid var(--border)",
                      paddingTop: "var(--s3)",
                      marginTop: "var(--s2)",
                      backgroundColor: "var(--bg-subtle, rgba(0,0,0,0.02))",
                      padding: "var(--s3)",
                      borderRadius: "var(--r-ctl)",
                    }}
                  >
                    <div className="row wrap-between" style={{ alignItems: "center" }}>
                      <span className="bold" style={{ color: "var(--blue-ink)" }}>
                        اطلاعات حقوق و دستمزد (محرمانه)
                      </span>
                      {!editingPay ? (
                        <Button
                          variant="ghost"
                          onClick={() => {
                            setEditBasePay(
                              currentFile.basePay !== null && currentFile.basePay !== undefined
                                ? String(currentFile.basePay)
                                : "",
                            );
                            setEditHourlyRate(
                              currentFile.hourlyRate !== null && currentFile.hourlyRate !== undefined
                                ? String(currentFile.hourlyRate)
                                : "",
                            );
                            setEditingPay(true);
                          }}
                        >
                          ویرایش حقوق
                        </Button>
                      ) : null}
                    </div>

                    {!editingPay ? (
                      <div
                        className="row wrap"
                        style={{ gap: "var(--s4)", marginTop: "var(--s2)", alignItems: "center" }}
                      >
                        <div>
                          <span className="muted t-cap">حقوق پایه ماهانه: </span>
                          <span className="bold num">
                            {currentFile.basePay !== null && currentFile.basePay !== undefined
                              ? formatMoney(currentFile.basePay, "rial")
                              : "ثبت نشده"}
                          </span>
                        </div>
                        <div>
                          <span className="muted t-cap">دستمزد ساعتی: </span>
                          <span className="bold num">
                            {currentFile.hourlyRate !== null && currentFile.hourlyRate !== undefined
                              ? formatMoney(currentFile.hourlyRate, "rial")
                              : "ثبت نشده"}
                          </span>
                        </div>
                      </div>
                    ) : (
                      <div className="stack" style={{ gap: "var(--s3)", marginTop: "var(--s3)" }}>
                        <div className="row wrap" style={{ gap: "var(--s3)" }}>
                          <Field
                            id="edit-base-pay"
                            label="حقوق پایه ماهانه (ریال)"
                            type="number"
                            value={editBasePay}
                            onChange={(e) => {
                              setEditBasePay(e.target.value);
                            }}
                          />
                          <Field
                            id="edit-hourly-rate"
                            label="دستمزد ساعتی (ریال)"
                            type="number"
                            value={editHourlyRate}
                            onChange={(e) => {
                              setEditHourlyRate(e.target.value);
                            }}
                          />
                        </div>
                        <div className="row" style={{ gap: "var(--s2)", justifyContent: "flex-end" }}>
                          <Button
                            variant="ghost"
                            onClick={() => {
                              setEditingPay(false);
                            }}
                          >
                            انصراف
                          </Button>
                          <Button
                            variant="primary"
                            onClick={() => {
                              updatePayAction.run(undefined);
                            }}
                            disabled={updatePayAction.busy}
                          >
                            {updatePayAction.busy ? "در حال ذخیره..." : "ذخیره حقوق"}
                          </Button>
                        </div>
                        <ErrorLine error={updatePayAction.error} />
                      </div>
                    )}
                  </div>
                )}

                {currentFile.notes && (
                  <div className="muted t-cap" style={{ marginTop: "var(--s2)" }}>
                    یادداشت: {currentFile.notes}
                  </div>
                )}
              </div>
            </div>

            {/* Documents Section */}
            <Panel
              title="مدارک و گواهینامه‌ها"
              actions={
                <Button
                  variant="primary"
                  onClick={() => {
                    setShowAddDocModal(true);
                  }}
                >
                  ثبت مدرک جدید
                </Button>
              }
            >
              <div className="card" style={{ padding: "0", overflow: "hidden" }}>
                <table className="tbl">
                  <thead>
                    <tr>
                      <th>نوع مدرک</th>
                      <th>شماره مدرک</th>
                      <th>تاریخ انقضا</th>
                      <th>وضعیت انقضا</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {(currentFile.documents ?? []).length === 0 ? (
                      <tr>
                        <td
                          colSpan={5}
                          style={{ textAlign: "center", padding: "var(--s3)" }}
                          className="muted"
                        >
                          مدرکی برای این شخص ثبت نشده است.
                        </td>
                      </tr>
                    ) : (
                      (currentFile.documents ?? []).map((doc) => {
                        const isExpired = doc.expiryStatus === "expired";
                        const isSoon = doc.expiryStatus === "expiring_soon";

                        return (
                          <tr key={doc.id}>
                            <td className="bold">{doc.kindLabel || doc.kind}</td>
                            <td className="num">{faDigits(doc.documentNumber)}</td>
                            <td className="num">{formatDate(doc.expiryDate)}</td>
                            <td>
                              {isExpired ? (
                                <span
                                  className="chip coral"
                                  data-status="expired"
                                  style={{ color: "var(--coral-ink)", background: "var(--coral-soft)" }}
                                >
                                  منقضی
                                </span>
                              ) : isSoon ? (
                                <span
                                  className="chip coral"
                                  data-status="expiring_soon"
                                  style={{ color: "var(--coral-ink)", background: "var(--coral-soft)" }}
                                >
                                  انقضا نزدیک است
                                </span>
                              ) : (
                                <span className="chip ok" data-status="valid">
                                  معتبر
                                </span>
                              )}
                            </td>
                            <td className="end">
                              <Button
                                variant="ghost"
                                size="md"
                                onClick={() => {
                                  archiveDocAction.run(doc);
                                }}
                                disabled={archiveDocAction.busy}
                              >
                                حذف
                              </Button>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </Panel>
          </div>
        </Panel>
      )}

      {/* Modal: Add Personnel File */}
      {showAddFileModal && (
        <div className="modal-backdrop">
          <div
            className="card modal-card"
            style={{ maxWidth: "600px", width: "95%", maxHeight: "90vh", overflowY: "auto" }}
          >
            <div className="row wrap-between" style={{ alignItems: "center", marginBottom: "var(--s3)" }}>
              <h2 className="t-title">افزودن پرونده پرسنلی جدید</h2>
              <Button
                variant="ghost"
                onClick={() => {
                  setShowAddFileModal(false);
                }}
              >
                بستن
              </Button>
            </div>

            <form
              className="stack"
              style={{ gap: "var(--s3)" }}
              onSubmit={(e) => {
                e.preventDefault();
                addFileAction.run(undefined);
              }}
            >
              <div>
                <label htmlFor="staff-select" className="label t-cap">
                  انتخاب پرسنل (از لیست کارکنان)
                </label>
                <select
                  id="staff-select"
                  className="input"
                  value={formPersonId}
                  onChange={(e) => {
                    const pid = e.target.value;
                    setFormPersonId(pid);
                    const found = (staffQuery.data ?? []).find((s) => s.id === pid);
                    if (found) {
                      setFormPersonName(found.displayName);
                    }
                  }}
                  required
                >
                  <option value="">-- انتخاب پرسنل --</option>
                  {(staffQuery.data ?? []).map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.displayName} ({s.phone})
                    </option>
                  ))}
                </select>
              </div>

              <Field
                id="person-name"
                label="نام و نام خانوادگی پرسنل"
                value={formPersonName}
                onChange={(e) => {
                  setFormPersonName(e.target.value);
                }}
                required
              />

              <div className="row wrap" style={{ gap: "var(--s3)" }}>
                <div style={{ flex: 1, minWidth: "200px" }}>
                  <label htmlFor="position-select" className="label t-cap">
                    سمت سازمانی
                  </label>
                  <select
                    id="position-select"
                    className="input"
                    value={formPositionId}
                    onChange={(e) => {
                      setFormPositionId(e.target.value);
                    }}
                  >
                    <option value="">-- بدون سمت / انتخاب بعدی --</option>
                    {(positionsQuery.data ?? []).map((pos) => (
                      <option key={pos.id} value={pos.id}>
                        {pos.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div style={{ flex: 1, minWidth: "200px" }}>
                  <label htmlFor="employment-kind" className="label t-cap">
                    نوع همکاری
                  </label>
                  <select
                    id="employment-kind"
                    className="input"
                    value={formEmploymentKind}
                    onChange={(e) => {
                      setFormEmploymentKind(e.target.value as PersonnelFileRow["employmentKind"]);
                    }}
                  >
                    <option value="official">رسمی</option>
                    <option value="contractual">قراردادی</option>
                    <option value="daily">روزمزد</option>
                    <option value="temporary">موقت</option>
                  </select>
                </div>
              </div>

              <div className="row wrap" style={{ gap: "var(--s3)" }}>
                <div style={{ flex: 1, minWidth: "200px" }}>
                  <Field
                    id="person-level"
                    label="رده شغلی (اختیاری)"
                    value={formLevel}
                    onChange={(e) => {
                      setFormLevel(e.target.value);
                    }}
                    placeholder="مثال: سرآشپز ارشد"
                  />
                </div>
                <div style={{ flex: 1, minWidth: "200px" }}>
                  <Field
                    id="person-start-date"
                    label="تاریخ شروع به کار (YYYY-MM-DD)"
                    type="date"
                    value={formStartDate}
                    onChange={(e) => {
                      setFormStartDate(e.target.value);
                    }}
                    required
                  />
                </div>
              </div>

              <div
                style={{
                  borderTop: "1px solid var(--border)",
                  paddingTop: "var(--s3)",
                  marginTop: "var(--s1)",
                }}
              >
                <h4 className="bold t-cap" style={{ marginBottom: "var(--s2)" }}>
                  اطلاعات تماس اضطراری
                </h4>
                <div className="row wrap" style={{ gap: "var(--s3)" }}>
                  <div style={{ flex: 1, minWidth: "160px" }}>
                    <Field
                      id="contact-name"
                      label="نام فرد"
                      value={formContactName}
                      onChange={(e) => {
                        setFormContactName(e.target.value);
                      }}
                      required
                    />
                  </div>
                  <div style={{ flex: 1, minWidth: "160px" }}>
                    <Field
                      id="contact-phone"
                      label="شماره تماس"
                      dir="ltr"
                      inputMode="tel"
                      value={formContactPhone}
                      onChange={(e) => {
                        setFormContactPhone(e.target.value);
                      }}
                      required
                    />
                  </div>
                  <div style={{ flex: 1, minWidth: "120px" }}>
                    <Field
                      id="contact-relation"
                      label="نسبت"
                      value={formContactRelation}
                      onChange={(e) => {
                        setFormContactRelation(e.target.value);
                      }}
                    />
                  </div>
                </div>
              </div>

              <div
                style={{
                  borderTop: "1px solid var(--border)",
                  paddingTop: "var(--s3)",
                  marginTop: "var(--s1)",
                }}
              >
                <h4 className="bold t-cap" style={{ marginBottom: "var(--s2)" }}>
                  حقوق و دستمزد (اختیاری — فقط مدیر ارشد)
                </h4>
                <div className="row wrap" style={{ gap: "var(--s3)" }}>
                  <div style={{ flex: 1, minWidth: "200px" }}>
                    <Field
                      id="base-pay"
                      label="حقوق پایه ماهانه (ریال)"
                      type="number"
                      value={formBasePay}
                      onChange={(e) => {
                        setFormBasePay(e.target.value);
                      }}
                    />
                  </div>
                  <div style={{ flex: 1, minWidth: "200px" }}>
                    <Field
                      id="hourly-rate"
                      label="دستمزد ساعتی (ریال)"
                      type="number"
                      value={formHourlyRate}
                      onChange={(e) => {
                        setFormHourlyRate(e.target.value);
                      }}
                    />
                  </div>
                </div>
              </div>

              <Field
                id="person-notes"
                label="یادداشت‌ها (اختیاری)"
                value={formNotes}
                onChange={(e) => {
                  setFormNotes(e.target.value);
                }}
              />

              <ErrorLine error={addFileAction.error} />

              <div className="row" style={{ gap: "var(--s2)", justifyContent: "flex-end" }}>
                <Button
                  variant="ghost"
                  onClick={() => {
                    setShowAddFileModal(false);
                  }}
                  disabled={addFileAction.busy}
                >
                  انصراف
                </Button>
                <Button variant="primary" type="submit" disabled={addFileAction.busy}>
                  {addFileAction.busy ? "در حال ایجاد..." : "ثبت پرونده پرسنلی"}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal: Add Document */}
      {showAddDocModal && currentFile && (
        <div className="modal-backdrop">
          <div className="card modal-card" style={{ maxWidth: "520px", width: "95%" }}>
            <div className="row wrap-between" style={{ alignItems: "center", marginBottom: "var(--s3)" }}>
              <h2 className="t-title">ثبت مدرک برای {currentFile.personName}</h2>
              <Button
                variant="ghost"
                onClick={() => {
                  setShowAddDocModal(false);
                }}
              >
                بستن
              </Button>
            </div>

            <form
              className="stack"
              style={{ gap: "var(--s3)" }}
              onSubmit={(e) => {
                e.preventDefault();
                addDocAction.run(undefined);
              }}
            >
              <div>
                <label htmlFor="doc-kind" className="label t-cap">
                  نوع مدرک
                </label>
                <select
                  id="doc-kind"
                  className="input"
                  value={docKind}
                  onChange={(e) => {
                    setDocKind(e.target.value);
                  }}
                  required
                >
                  {(docTypesQuery.data ?? []).map((dt) => (
                    <option key={dt.id} value={dt.code}>
                      {dt.name} ({dt.code})
                    </option>
                  ))}
                  {/* Fallback option if doc types not loaded or custom */}
                  <option value="health_card">کارت بهداشت</option>
                  <option value="id_card">شناسنامه و کارت ملی</option>
                  <option value="contract">قرارداد کاری</option>
                  <option value="driving_license">گواهینامه رانندگی</option>
                  <option value="insurance_record">سوابق بیمه</option>
                </select>
              </div>

              <Field
                id="doc-number"
                label="شماره مدرک"
                value={docNumber}
                onChange={(e) => {
                  setDocNumber(e.target.value);
                }}
                required
                placeholder="مثال: ۱۲۳۴۵۶"
              />

              <div className="row wrap" style={{ gap: "var(--s3)" }}>
                <div style={{ flex: 1, minWidth: "180px" }}>
                  <Field
                    id="doc-issue-date"
                    label="تاریخ صدور (اختیاری)"
                    type="date"
                    value={docIssueDate}
                    onChange={(e) => {
                      setDocIssueDate(e.target.value);
                    }}
                  />
                </div>
                <div style={{ flex: 1, minWidth: "180px" }}>
                  <Field
                    id="doc-expiry-date"
                    label="تاریخ انقضا (الزامی)"
                    type="date"
                    value={docExpiryDate}
                    onChange={(e) => {
                      setDocExpiryDate(e.target.value);
                    }}
                    required
                  />
                </div>
              </div>

              <Field
                id="doc-notes"
                label="توضیحات (اختیاری)"
                value={docNotes}
                onChange={(e) => {
                  setDocNotes(e.target.value);
                }}
              />

              <ErrorLine error={addDocAction.error} />

              <div className="row" style={{ gap: "var(--s2)", justifyContent: "flex-end" }}>
                <Button
                  variant="ghost"
                  onClick={() => {
                    setShowAddDocModal(false);
                  }}
                  disabled={addDocAction.busy}
                >
                  انصراف
                </Button>
                <Button variant="primary" type="submit" disabled={addDocAction.busy}>
                  {addDocAction.busy ? "در حال ثبت..." : "تأیید و ثبت مدرک"}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
